// The gallery thumbnail studio. One shared WebGL renderer draws every tile's
// 3D preview and blits the result into each tile's own lightweight 2D canvas.
// A single GL context (rather than one per tile) is what keeps the gallery
// scalable as the model library grows — browsers cap the number of live WebGL
// contexts, but 2D canvases are cheap. The studio caches the model it last drew
// so a hover-spin re-renders the same instance frame after frame.

import * as THREE from "three";
import { instantiateModel, ModelInstance, type ColorRepresentation, type Model } from "../lib";

const SIZE = 480; // square render resolution in device pixels
const PITCH = THREE.MathUtils.degToRad(28);
const AZIM = THREE.MathUtils.degToRad(35);

interface Current {
  model: Model;
  variantId: string;
  /** Serialised slot overrides this instance was built with (cache key). */
  slotsKey: string;
  instance: ModelInstance;
  target: THREE.Vector3;
  /** Half-extent the framing is built from (max of footprint radius and 0.6·height). */
  extent: number;
}

export class ThumbStudio {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -100, 200);
  private readonly pivot = new THREE.Object3D();
  private current?: Current;

  constructor() {
    // `preserveDrawingBuffer` guarantees the rendered frame is still readable
    // when we `drawImage` it onto a tile in the same tick.
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(SIZE, SIZE, false);
    this.renderer.setClearColor(0x000000, 0); // transparent — the card shows through
    this.scene.add(this.pivot);
  }

  /** Render `model`/`variantId` at yaw `yaw` (radians) into the tile `target`.
   *  `tight` drops the fixed padding so the subject fills the tile consistently
   *  whatever its size — the proportional fit margin is kept, so it is never
   *  cropped (small props stop looking lost, big buildings stop overflowing). */
  draw(target: HTMLCanvasElement, model: Model, variantId: string, yaw: number, tight = false, slots?: Record<string, ColorRepresentation>): void {
    const cur = this.use(model, variantId, slots);
    this.pivot.rotation.set(0, yaw, 0);

    const c = this.camera;
    const zoom = cur.extent * 1.4 + (tight ? 0 : 0.15);
    c.top = zoom; c.bottom = -zoom; c.left = -zoom; c.right = zoom; // square
    const cp = Math.cos(PITCH), sp = Math.sin(PITCH);
    c.position.set(cur.target.x + 40 * cp * Math.sin(AZIM), cur.target.y + 40 * sp, cur.target.z + 40 * cp * Math.cos(AZIM));
    c.up.set(0, 1, 0);
    c.lookAt(cur.target);
    c.updateProjectionMatrix();

    this.renderer.render(this.scene, this.camera);
    const ctx = target.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, target.width, target.height);
    ctx.drawImage(this.renderer.domElement, 0, 0, target.width, target.height);
  }

  // Reuse the cached instance while the model/variant/slots are unchanged;
  // otherwise rebuild, recentre on the pivot axis and reframe the camera to fit.
  private use(model: Model, variantId: string, slots?: Record<string, ColorRepresentation>): Current {
    const slotsKey = slots ? JSON.stringify(slots) : "";
    if (this.current && this.current.model === model && this.current.variantId === variantId && this.current.slotsKey === slotsKey) return this.current;
    this.current?.instance.dispose();
    this.pivot.clear();

    // Unlit material: buildModel already bakes the faceted shading into the
    // vertex colours, so no scene lights (and no vertex normals) are needed.
    const instance = instantiateModel(model, { material: new THREE.MeshBasicMaterial({ vertexColors: true }), slots });
    instance.setVariant(variantId);
    instance.setAnimation(null);
    this.pivot.add(instance.object);

    const bb = instance.bounds;
    const cx = (bb.min.x + bb.max.x) / 2, cz = (bb.min.z + bb.max.z) / 2;
    instance.object.position.set(-cx, -bb.min.y, -cz);
    const radius = Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z) / 2 || 0.2;
    const height = Math.max(bb.max.y - bb.min.y, 0.1);

    this.current = {
      model, variantId, slotsKey, instance,
      target: new THREE.Vector3(0, height * 0.5, 0),
      extent: Math.max(radius, height * 0.6),
    };
    return this.current;
  }
}
